import React, { useEffect, useState } from "react";
import {
  Zap,
  AlertCircle,
  CheckCircle2,
  Search,
  Play,
  RefreshCw,
  ExternalLink,
  Loader2,
  Globe2,
  Shield,
  Tag,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { timeAgo } from "@/lib/time";

const POS_LABELS = {
  dutchie: "Dutchie",
  jane: "Jane",
  blaze: "BLAZE",
  treez: "Treez",
  sweed: "Sweed",
  trulieve: "Trulieve",
};

const POS_COLOR = {
  dutchie: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  jane: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  blaze: "bg-orange-500/15 text-orange-300 border-orange-500/40",
  treez: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
  sweed: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  trulieve: "bg-[#d4a268]/15 text-[#d4a268] border-[#d4a268]/40",
};

export const PublicIngestTab = () => {
  const [status, setStatus] = useState(null);
  const [shops, setShops] = useState(null);
  const [sched, setSched] = useState(null);
  const [busy, setBusy] = useState(false);
  const [perShopBusy, setPerShopBusy] = useState({});
  const [incidents, setIncidents] = useState([]);

  const refresh = async () => {
    const [s, sh, sc, inc] = await Promise.all([
      api.get("/admin/public-ingest/status"),
      api.get("/admin/public-ingest/shops"),
      api.get("/admin/scheduler/status").catch(() => ({ data: null })),
      api.get("/admin/ingest/incidents").catch(() => ({ data: { incidents: [] } })),
    ]);
    setStatus(s.data);
    setShops(sh.data);
    setSched(sc.data);
    setIncidents(inc.data?.incidents || []);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, sh, sc, inc] = await Promise.all([
          api.get("/admin/public-ingest/status"),
          api.get("/admin/public-ingest/shops"),
          api.get("/admin/scheduler/status").catch(() => ({ data: null })),
          api.get("/admin/ingest/incidents").catch(() => ({ data: { incidents: [] } })),
        ]);
        if (cancelled) return;
        setStatus(s.data);
        setShops(sh.data);
        setSched(sc.data);
        setIncidents(inc.data?.incidents || []);
      } catch {
        /* ignore */
      }
    })();
    // Poll incidents every 30s so a fresh alert surfaces without a manual
    // refresh. Cheap query — one indexed lookup on `resolved_at`.
    const t = setInterval(async () => {
      try {
        const inc = await api.get("/admin/ingest/incidents");
        if (!cancelled) setIncidents(inc.data?.incidents || []);
      } catch {
        /* ignore */
      }
    }, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const ackIncident = async (id) => {
    try {
      await api.post(`/admin/ingest/incidents/${id}/ack`);
      setIncidents((prev) => prev.filter((i) => i._id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runAll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/public-ingest/run-all");
      const t = r.data.totals;
      const totalOk = Object.values(t).reduce((s, x) => s + (x.ok || 0), 0);
      const totalErr = Object.values(t).reduce((s, x) => s + (x.err || 0), 0);
      toast.success(`Ingest run: ${totalOk} ok · ${totalErr} errors`, {
        description: `Scanned ${r.data.scanned} shops`,
      });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const detect = async (slug) => {
    setPerShopBusy((m) => ({ ...m, [slug]: "detect" }));
    try {
      const r = await api.post(`/admin/public-ingest/detect/${slug}`);
      toast.success(
        r.data.pos_detected
          ? `Detected: ${POS_LABELS[r.data.pos_detected] || r.data.pos_detected}`
          : "Couldn't detect POS — add a website URL first"
      );
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPerShopBusy((m) => ({ ...m, [slug]: null }));
    }
  };

  const runOne = async (slug) => {
    setPerShopBusy((m) => ({ ...m, [slug]: "run" }));
    try {
      const r = await api.post(`/admin/public-ingest/run/${slug}`);
      if (r.data.ok) {
        toast.success(
          `${r.data.products_upserted} products synced via ${r.data.pos}`
        );
      } else {
        toast.error(r.data.error || "Sync failed", { description: r.data.pos });
      }
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPerShopBusy((m) => ({ ...m, [slug]: null }));
    }
  };

  if (!status || !shops)
    return (
      <div className="text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
        Loading public-ingest dashboard…
      </div>
    );

  return (
    <div className="space-y-6">
      {/* Ingest incident banner — auto-refreshes every 30s. Critical
          issues render in red, warnings in amber. Ack removes them from
          the banner until the underlying condition worsens or resolves. */}
      {incidents.length > 0 && (
        <div data-testid="ingest-incidents-banner" className="space-y-2">
          {incidents.map((i) => {
            const critical = i.severity === "critical";
            return (
              <div
                key={i._id}
                data-testid={`ingest-incident-${i._id}`}
                className={`flex items-start gap-3 rounded-2xl border p-4 ${
                  critical
                    ? "bg-destructive/10 border-destructive/40 text-destructive"
                    : "bg-amber-500/10 border-amber-500/40 text-amber-200"
                }`}
              >
                <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={2} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-semibold">
                      {i.severity || "info"}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-foreground/60">
                      · {i.source || "unknown"} ·{" "}
                      {(i.kind || "issue").replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="text-sm mt-1 leading-relaxed text-foreground/90">
                    {i.detail || "(no details)"}
                  </div>
                  {i.opened_at && (
                    <div className="text-[11px] text-foreground/50 mt-1">
                      Opened {timeAgo(i.opened_at)}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  data-testid={`ingest-incident-ack-${i._id}`}
                  onClick={() => ackIncident(i._id)}
                  className="text-[11px] uppercase tracking-wider px-3 h-8 rounded-full border border-current opacity-80 hover:opacity-100 whitespace-nowrap"
                >
                  Ack
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow mb-1 flex items-center gap-2">
            <Globe2 className="w-3.5 h-3.5 text-accent" />
            Public-menu ingest
          </div>
          <h3 className="font-serif text-2xl">
            Auto-index public dispensary menus
          </h3>
          <p className="text-sm text-foreground/60 max-w-2xl mt-1">
            Five POS adapters (Dutchie, Jane, BLAZE, Treez, Sweed) hit the same
            unauthenticated JSON endpoints the shop&apos;s own storefront calls.
            Polite 15-minute cooldown, source attribution shown to consumers,
            owners can opt out from their dashboard.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={refresh}
            data-testid="public-ingest-refresh"
            className="pill h-9 text-xs"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <button
            type="button"
            onClick={runAll}
            disabled={busy}
            data-testid="public-ingest-run-all"
            className="pill pill-primary h-9 text-xs"
          >
            {busy ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Play className="w-3 h-3" />
            )}
            Run all
          </button>
        </div>
      </div>

      {/* per-source rollup */}
      <div
        className="grid grid-cols-2 lg:grid-cols-6 gap-3"
        data-testid="public-ingest-stats"
      >
        {Object.entries(status.by_source).map(([pos, s]) => {
          const awaitingPartner = (pos === "dutchie" || pos === "jane") && s.shops_err > 0 && s.shops_ok === 0;
          return (
          <div
            key={pos}
            className="border border-border bg-card rounded-2xl p-4"
            data-testid={`public-ingest-stat-${pos}`}
          >
            <div className="flex items-center justify-between mb-1.5">
              <span
                className={`pill text-[10px] ${POS_COLOR[pos] || ""}`}
              >
                {s.label}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {s.shops_detected}
              </span>
            </div>
            <div className="font-serif text-2xl">{s.products}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {awaitingPartner ? (
                <span className="text-sky-300 inline-flex items-center gap-1">
                  <Shield className="w-3 h-3" /> awaiting partner API
                </span>
              ) : (
                <>{s.shops_ok} ok · {s.shops_err} err</>
              )}
            </div>
          </div>
          );
        })}
        <div className="border border-dashed border-border bg-card/40 rounded-2xl p-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <AlertCircle className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Unknown POS
            </span>
          </div>
          <div className="font-serif text-2xl">{status.unknown_shops}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Need a website URL
          </div>
        </div>
      </div>

      {status.last_run_at && (
        <p className="text-xs text-muted-foreground">
          Last run · {timeAgo(status.last_run_at)}
        </p>
      )}

      {/* Scheduler card */}
      {sched?.running && (
        <div
          className="border border-border bg-card rounded-2xl px-5 py-4 flex items-center justify-between gap-3"
          data-testid="public-ingest-scheduler"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 inline-flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-medium">Nightly auto-sync running</div>
              <div className="text-xs text-muted-foreground">
                Cron <code className="text-foreground/70">{sched.cron}</code>
                {sched.next_run_at && (
                  <>
                    {" "}· next run{" "}
                    <span className="text-foreground/80">
                      {new Date(sched.next_run_at).toLocaleString(undefined, {
                        weekday: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                        timeZoneName: "short",
                      })}
                    </span>
                  </>
                )}
                {sched.last_status?.finished_at && (
                  <>
                    {" "}· last finished {timeAgo(sched.last_status.finished_at)}
                    {sched.last_status.scanned !== undefined && (
                      <>
                        {" "}({sched.last_status.scanned} scanned ·{" "}
                        {sched.last_status.ok} ok · {sched.last_status.err} err)
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="public-ingest-refresh-specials"
              onClick={async () => {
                setBusy(true);
                try {
                  const { data } = await api.post("/admin/specials/refresh");
                  toast.success(
                    `Specials synced · ${data?.shops_synced ?? 0} shops · ${data?.upserted ?? 0} banners`
                  );
                  refresh();
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="pill text-xs h-9"
              title="Pull Trulieve's public /specials feed for every wired-up store and refresh promo banners now"
            >
              {busy ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Tag className="w-3 h-3" />
              )}
              Refresh specials now
            </button>
            <button
              type="button"
              data-testid="public-ingest-trigger-now"
              onClick={async () => {
                setBusy(true);
                try {
                  await api.post("/admin/scheduler/trigger-now");
                  toast.success("Cron ran — refreshing");
                  refresh();
                } catch (e) {
                  toast.error(formatApiError(e));
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="pill text-xs h-9"
            >
              {busy ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Trigger nightly job now
            </button>
          </div>
        </div>
      )}

      {/* per-shop table */}
      <div className="border border-border bg-card rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h4 className="font-serif text-lg">
            All shops · {shops.count}
          </h4>
          <span className="text-xs text-muted-foreground">
            polite cooldown 15 min/shop
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-secondary/40 text-left">
              <tr>
                <th className="px-4 py-2.5 font-medium">Shop</th>
                <th className="px-4 py-2.5 font-medium">POS</th>
                <th className="px-4 py-2.5 font-medium">Source</th>
                <th className="px-4 py-2.5 font-medium text-right">Public products</th>
                <th className="px-4 py-2.5 font-medium">Last sync</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {shops.shops.map((s) => (
                <tr
                  key={s.slug}
                  data-testid={`public-ingest-row-${s.slug}`}
                  className="hover:bg-secondary/30"
                >
                  <td className="px-4 py-2.5 align-top">
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {s.city || "—"}
                      {s.website_url && (
                        <a
                          href={s.website_url}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-2 text-foreground/60 hover:text-foreground inline-flex items-center"
                        >
                          site <ExternalLink className="w-2.5 h-2.5 ml-0.5" />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 align-top">
                    {s.pos_detected ? (
                      <span
                        className={`pill text-[10px] ${
                          POS_COLOR[s.pos_detected] || ""
                        }`}
                      >
                        {POS_LABELS[s.pos_detected] || s.pos_detected}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 align-top">
                    {s.disabled ? (
                      <span className="pill text-[10px] bg-rose-500/15 text-rose-300 border-rose-500/40">
                        <Shield className="w-3 h-3" /> Opt-out
                      </span>
                    ) : s.is_owner_managed ? (
                      <span className="pill text-[10px] bg-foreground text-background border-transparent">
                        <Zap className="w-3 h-3" /> Owner-managed
                      </span>
                    ) : s.is_public_ingest ? (
                      <span className="pill text-[10px] bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
                        <Globe2 className="w-3 h-3" /> Public ingest
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        Listing only
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 align-top text-right font-mono">
                    {s.public_products}
                  </td>
                  <td className="px-4 py-2.5 align-top text-xs text-muted-foreground">
                    {s.last_synced_at ? (
                      <>
                        {timeAgo(s.last_synced_at)} ·{" "}
                        {s.last_sync_status === "ok" ? (
                          <span className="text-emerald-300 inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> ok
                          </span>
                        ) : (s.pos_detected === "dutchie" || s.pos_detected === "jane") ? (
                          <span
                            className="text-sky-300 inline-flex items-center gap-1"
                            title="Public scrape blocked by Cloudflare — waiting on official Partner API credentials"
                          >
                            <Shield className="w-3 h-3" /> awaiting partner API
                          </span>
                        ) : (
                          <span className="text-rose-300 inline-flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> err
                          </span>
                        )}
                      </>
                    ) : (
                      "never"
                    )}
                  </td>
                  <td className="px-4 py-2.5 align-top">
                    <div className="flex gap-1.5 justify-end">
                      <button
                        type="button"
                        data-testid={`public-ingest-detect-${s.slug}`}
                        onClick={() => detect(s.slug)}
                        disabled={!!perShopBusy[s.slug]}
                        className="pill text-[10px]"
                        title="Re-detect POS"
                      >
                        {perShopBusy[s.slug] === "detect" ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Search className="w-3 h-3" />
                        )}
                        Detect
                      </button>
                      <button
                        type="button"
                        data-testid={`public-ingest-run-${s.slug}`}
                        onClick={() => runOne(s.slug)}
                        disabled={!!perShopBusy[s.slug] || !s.pos_detected || s.disabled || s.is_owner_managed}
                        className="pill pill-primary text-[10px] disabled:opacity-40"
                        title={
                          s.is_owner_managed
                            ? "Skipped — owner manages this menu"
                            : s.disabled
                              ? "Owner opted out"
                              : !s.pos_detected
                                ? "Detect POS first"
                                : "Run ingest now"
                        }
                      >
                        {perShopBusy[s.slug] === "run" ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Play className="w-3 h-3" />
                        )}
                        Run
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {shops.shops.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    No real shops yet — concierge-onboard a few first.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
